// Type for content property value structure
export type BylineContentPropertyValue = {
	title?: string;
	icon?: string;
	tooltip?: string;
};

// Utility function to check if a value is a non-empty string
const isNonEmptyString = (value: any): value is string => {
	return typeof value === 'string' && value.trim() !== '';
};

// Utility function to validate and set a byline content property value
const validatePropertyValue = (
	key: keyof BylineContentPropertyValue,
	value: any,
	validatedProperties: BylineContentPropertyValue,
) => {
	if (value !== undefined) {
		if (isNonEmptyString(value)) {
			validatedProperties[key] = value;
		} else {
			// eslint-disable-next-line no-console
			console.warn(`Byline content property ${key} must be a non-empty string, got:`, typeof value);
		}
	}
};

// Validation function for content property data
export const validateContentPropertyData = (
	data: any,
	defaultValues: { title: string; icon: string; tooltip: string },
): { title: string; icon: string; tooltip: string } => {
	try {
		// Check if data exists and has the expected structure
		if (
			!data ||
			!data.content ||
			!data.content.nodes ||
			data.content.nodes.length === 0 ||
			!data.content.nodes[0].properties ||
			!data.content.nodes[0].properties.nodes ||
			data.content.nodes[0].properties.nodes.length === 0
		) {
			// eslint-disable-next-line no-console
			console.warn('Invalid byline content property query data');
			return defaultValues;
		}

		const propertyNode = data.content.nodes[0].properties.nodes[0];

		if (!propertyNode.value) {
			// eslint-disable-next-line no-console
			console.warn('Byline content property data has no value');
			return defaultValues;
		}

		// Parse JSON with error handling
		let propertyValue: BylineContentPropertyValue;
		try {
			propertyValue = JSON.parse(propertyNode.value) as BylineContentPropertyValue;
		} catch (jsonError) {
			// eslint-disable-next-line no-console
			console.error('Failed to parse byline content property JSON:', jsonError);
			return defaultValues;
		}

		// Validate the structure of the parsed value
		if (typeof propertyValue !== 'object' || propertyValue === null) {
			// eslint-disable-next-line no-console
			console.error('Byline content property value is not an object:', propertyValue);
			return defaultValues;
		}

		const validatedProperties = { ...defaultValues };

		// Validate each property using the byline content property value utility function
		validatePropertyValue('title', propertyValue.title, validatedProperties);
		validatePropertyValue('icon', propertyValue.icon, validatedProperties);
		validatePropertyValue('tooltip', propertyValue.tooltip, validatedProperties);

		return validatedProperties;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Unexpected error in content property validation:', error);
		return defaultValues;
	}
};
